import {
  Container,
  TextInput,
  NumberInput,
  Button,
  Stack,
  Group,
  Text,
  Alert,
  Card,
  Title,
  Badge,
  Checkbox,
  Select,
  Divider,
  Paper,
  Loader,
  Center,
  FileInput,
  ActionIcon,
  Table,
} from '@mantine/core';
import { useForm } from '@mantine/form';
import { notifications } from '@mantine/notifications';
import { IconAlertCircle, IconArrowLeft, IconDeviceFloppy, IconUpload, IconX, IconFile, IconCheck, IconClock, IconFileText, IconDownload } from '@tabler/icons-react';
import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { bidSubmissionApi, CreateBidSubmissionDto } from '../../../api/bidSubmissions';
import { bidSubmissionDocumentsApi, BidSubmissionDocument } from '../../../api/bidSubmissionDocuments';
import { useVendorTenderRequirements } from '../hooks/useTenderRequirements';
import { useTenderItems } from '../../tenders/hooks/useTenderItems';
import { useVendorProfile } from '../hooks/useVendors';
import { useVendorDocuments } from '../hooks/useVendorDocuments';
import { useTenderRegistrationStatus } from '../hooks/useTenderRegistrations';
import { useTenderById } from '../../tenders/hooks/useTenders';
import { publicTenderApi } from '../../../api/publicTenders';
import { getCurrencySymbol, formatCurrency } from '../../../utils/currency';

export function VendorBidSubmissionPage() {
  const { tenderId } = useParams<{ tenderId: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSavingDraft, setIsSavingDraft] = useState(false);
  
  const { data: vendorProfile } = useVendorProfile();
  const { data: vendorDocuments } = useVendorDocuments();
  const { data: tender } = useTenderById(tenderId!);
  const { data: publicTender } = useQuery({
    queryKey: ['public-tender-detail', tenderId],
    queryFn: () => publicTenderApi.getTenderById(tenderId!),
    enabled: !!tenderId,
  });
  const { data: registrationStatus } = useTenderRegistrationStatus(tenderId!);
  const { data: requirements, isLoading: loadingRequirements } = useVendorTenderRequirements(tenderId!);
  const { data: tenderItems, isLoading: loadingItems } = useTenderItems(tenderId!, true); // Use vendor endpoint

  // Check for any existing submission for this tender
  const { data: vendorSubmissions } = useQuery({
    queryKey: ['vendor-bid-submissions'],
    queryFn: () => bidSubmissionApi.getVendorBidSubmissions(),
    enabled: !!vendorProfile,
  });

  const existingSubmissionForTender = vendorSubmissions?.find(
    sub => sub.tenderId === tenderId && sub.deletedAt === null
  );

  // Fetch draft bid submission - create if doesn't exist
  const { data: draftSubmission, isLoading: loadingDraft, refetch: refetchDraft } = useQuery({
    queryKey: ['bid-submission-draft', tenderId],
    queryFn: async () => {
      try {
        return await bidSubmissionApi.getDraftBidSubmission(tenderId!, true); // Use vendor auth
      } catch (error: any) {
        // If no draft exists and no submitted bid exists, create one automatically
        if (error.response?.status === 404 && vendorProfile && !existingSubmissionForTender) {
          const draftData: CreateBidSubmissionDto = {
            companyName: vendorProfile.businessName || '',
            totalPrice: 0,
            representativeName: '',
            representativeTitle: '',
            representativePhone: vendorProfile.phone || '',
            representativeEmail: vendorProfile.email || '',
            companyAddress: vendorProfile.address || '',
            registrationNumber: vendorProfile.registrationNumber || '',
            taxId: vendorProfile.taxId || '',
            status: 'SAVED' as const,
            bidItems: [],
          };
          return await bidSubmissionApi.createBidSubmission(tenderId!, draftData, true); // Use vendor auth
        }
        throw error;
      }
    },
    enabled: !!tenderId && !!vendorProfile && !existingSubmissionForTender,
  });

  // Fetch uploaded documents for this bid submission
  const { data: uploadedDocuments, refetch: refetchDocuments, dataUpdatedAt } = useQuery({
    queryKey: ['bid-submission-documents', draftSubmission?.id],
    queryFn: async () => {
      // #region agent log
      if (typeof window !== 'undefined') {
        fetch('http://127.0.0.1:7242/ingest/7c805428-0cbf-4a21-923a-5d22bb0a3d78',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({location:'VendorBidSubmissionPage.tsx:100',message:'Query function called',data:{draftSubmissionId:draftSubmission?.id},timestamp:Date.now(),sessionId:'debug-session',runId:'run1',hypothesisId:'E'})}).catch(()=>{});
      }
      // #endregion
      const result = await bidSubmissionDocumentsApi.listDocuments(draftSubmission!.id);
      // #region agent log
      if (typeof window !== 'undefined') {
        fetch('http://127.0.0.1:7242/ingest/7c805428-0cbf-4a21-923a-5d22bb0a3d78',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({location:'VendorBidSubmissionPage.tsx:106',message:'Query function returned',data:{draftSubmissionId:draftSubmission?.id,resultCount:result?.length,resultDocumentTypes:result?.map(d=>d.documentType)},timestamp:Date.now(),sessionId:'debug-session',runId:'run1',hypothesisId:'E'})}).catch(()=>{});
      }
      // #endregion
      return result;
    },
    enabled: !!draftSubmission?.id,
    refetchOnMount: true, // Always refetch when component mounts to ensure we have latest documents
    refetchOnWindowFocus: false, // Don't refetch on window focus
  });

  // #region agent log
  useEffect(() => {
    if (typeof window !== 'undefined') {
      fetch('http://127.0.0.1:7242/ingest/7c805428-0cbf-4a21-923a-5d22bb0a3d78',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({location:'VendorBidSubmissionPage.tsx:114',message:'Documents query state changed',data:{draftSubmissionId:draftSubmission?.id,uploadedDocumentsCount:uploadedDocuments?.length,enabled:!!draftSubmission?.id,dataUpdatedAt,allDocumentTypes:uploadedDocuments?.map(d=>d.documentType)},timestamp:Date.now(),sessionId:'debug-session',runId:'run1',hypothesisId:'E'})}).catch(()=>{});
    }
  }, [uploadedDocuments, dataUpdatedAt]);
  // #endregion

  // State for bid items
  const [bidItems, setBidItems] = useState<Array<{
    tenderItemId: string;
    itemName: string;
    description?: string;
    quantity: number;
    unit: string;
    price: number;
    order: number;
  }>>([]);

  // State for document selection (vendor documents)
  const [selectedDocuments, setSelectedDocuments] = useState<Record<string, string>>({});
  
  // State for file uploads (new files to upload)
  const [uploadedFiles, setUploadedFiles] = useState<Record<string, File | null>>({});
  
  // State for uploaded document IDs (documents already uploaded to bid submission)
  const [uploadedDocumentIds, setUploadedDocumentIds] = useState<Record<string, string>>({});
  
  // State for uploading status
  const [uploadingDocuments, setUploadingDocuments] = useState<Record<string, boolean>>({});

  const form = useForm<CreateBidSubmissionDto>({
    initialValues: {
      companyName: '',
      totalPrice: 0,
      representativeName: '',
      representativeTitle: '',
      representativePhone: '',
      representativeEmail: '',
      companyAddress: '',
      registrationNumber: '',
      taxId: '',
      hasTOR: false,
      hasFinancialStatement: false,
      hasTaxClearance: false,
      hasInsurance: false,
      hasExperience: false,
      hasTechnicalProposal: false,
      hasFinancialProposal: false,
      bidItems: [],
    },
    validate: {
      companyName: (value) => (!value ? 'Company name is required' : null),
      totalPrice: (value) => (value <= 0 ? 'Total price must be greater than 0' : null),
      representativeEmail: (value) =>
        value && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value) ? 'Invalid email format' : null,
    },
  });

  // Load draft submission if exists
  useEffect(() => {
    if (draftSubmission && !loadingDraft && vendorProfile) {
      form.setValues({
        // Company information always comes from vendor profile (non-editable)
        companyName: vendorProfile.businessName || '',
        companyAddress: vendorProfile.address || '',
        registrationNumber: vendorProfile.registrationNumber || '',
        taxId: vendorProfile.taxId || '',
        // Other fields from draft
        totalPrice: Number(draftSubmission.totalPrice) || 0,
        representativeName: draftSubmission.representativeName || '',
        representativeTitle: draftSubmission.representativeTitle || '',
        representativePhone: draftSubmission.representativePhone || vendorProfile.phone || '',
        representativeEmail: draftSubmission.representativeEmail || vendorProfile.email || '',
        hasTOR: draftSubmission.hasTOR || false,
        hasFinancialStatement: draftSubmission.hasFinancialStatement || false,
        hasTaxClearance: draftSubmission.hasTaxClearance || false,
        hasInsurance: draftSubmission.hasInsurance || false,
        hasExperience: draftSubmission.hasExperience || false,
        hasTechnicalProposal: draftSubmission.hasTechnicalProposal || false,
        hasFinancialProposal: draftSubmission.hasFinancialProposal || false,
      });

      // Load bid items from draft
      if (draftSubmission.bidItems && draftSubmission.bidItems.length > 0) {
        const loadedBidItems = draftSubmission.bidItems.map((item: any) => ({
          tenderItemId: item.tenderItemId,
          itemName: item.tenderItem?.itemName || '',
          description: item.tenderItem?.description,
          quantity: item.quantity,
          unit: item.unit,
          price: Number(item.price),
          order: item.order,
        }));
        setBidItems(loadedBidItems);
      }
    } else if (vendorProfile && !draftSubmission && !loadingDraft && bidItems.length === 0) {
      // Initialize form with vendor profile data if no draft exists
      form.setValues({
        companyName: vendorProfile.businessName || '',
        representativeName: '',
        representativeTitle: '',
        representativePhone: vendorProfile.phone || '',
        representativeEmail: vendorProfile.email || '',
        companyAddress: vendorProfile.address || '',
        registrationNumber: vendorProfile.registrationNumber || '',
        taxId: vendorProfile.taxId || '',
      });
    }
  }, [draftSubmission, vendorProfile, loadingDraft, bidItems.length]);

  // Always sync company information from vendor profile (non-editable fields)
  useEffect(() => {
    if (vendorProfile) {
      form.setFieldValue('companyName', vendorProfile.businessName || '');
      form.setFieldValue('companyAddress', vendorProfile.address || '');
      form.setFieldValue('registrationNumber', vendorProfile.registrationNumber || '');
      form.setFieldValue('taxId', vendorProfile.taxId || '');
    }
  }, [vendorProfile]);

  // Initialize bid items when tender items are loaded (only if no draft exists)
  useEffect(() => {
    // Don't initialize if we're still loading the draft
    if (loadingDraft) {
      return;
    }

    // If draft exists and has bid items, they're already loaded in the draft loading effect
    if (draftSubmission && draftSubmission.bidItems && draftSubmission.bidItems.length > 0) {
      return;
    }

    // Initialize from tender items if we have them
    if (tenderItems && tenderItems.length > 0) {
      setBidItems((currentBidItems) => {
        // Only initialize if bidItems is empty
        if (currentBidItems.length === 0) {
          return tenderItems.map((item) => ({
            tenderItemId: item.id,
            itemName: item.itemName,
            description: item.description,
            quantity: item.quantity,
            unit: item.unit,
            price: 0,
            order: item.order,
          }));
        }
        return currentBidItems;
      });
    }
  }, [tenderItems, draftSubmission, loadingDraft]);

  // Calculate total price when bid items change
  useEffect(() => {
    const totalPrice = bidItems.reduce((sum, item) => sum + (item.price * item.quantity), 0);
    form.setFieldValue('totalPrice', totalPrice);
  }, [bidItems]);

  const updateBidItem = (index: number, field: string, value: any) => {
    const updatedBidItems = [...bidItems];
    updatedBidItems[index] = { ...updatedBidItems[index], [field]: value };
    setBidItems(updatedBidItems);
  };

  const saveDraftMutation = useMutation({
    mutationFn: async (values: CreateBidSubmissionDto) => {
      const submissionData = {
        ...values,
        status: 'SAVED' as const,
        bidItems: bidItems.map(item => ({
          tenderItemId: item.tenderItemId,
          quantity: item.quantity,
          unit: item.unit,
          price: item.price,
          order: item.order,
        })),
      };
      return await bidSubmissionApi.createBidSubmission(tenderId!, submissionData, true); // Use vendor auth
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['bid-submission-draft', tenderId] });
      notifications.show({
        title: 'Draft Saved',
        message: 'Your bid has been saved as draft. You can continue editing later.',
        color: 'blue',
      });
    },
    onError: (error: any) => {
      notifications.show({
        title: 'Error',
        message: error.response?.data?.error || 'Failed to save draft. Please try again.',
        color: 'red',
      });
    },
  });

  // Function to upload a document for a requirement immediately
  const uploadDocumentForRequirement = async (requirementId: string, documentType: string, file?: File, vendorDocumentId?: string) => {
    // #region agent log
    if (typeof window !== 'undefined') {
      fetch('http://127.0.0.1:7242/ingest/7c805428-0cbf-4a21-923a-5d22bb0a3d78',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({location:'VendorBidSubmissionPage.tsx:295',message:'Upload function called',data:{requirementId,documentType,hasFile:!!file,hasVendorDocId:!!vendorDocumentId,draftSubmissionId:draftSubmission?.id,uploadedDocumentsCountBefore:uploadedDocuments?.length},timestamp:Date.now(),sessionId:'debug-session',runId:'run1',hypothesisId:'A'})}).catch(()=>{});
    }
    // #endregion

    if (!draftSubmission?.id) {
      notifications.show({
        title: 'Error',
        message: 'Bid submission not found. Please refresh the page.',
        color: 'red',
      });
      return;
    }

    setUploadingDocuments(prev => ({ ...prev, [requirementId]: true }));
    try {
      const formData = new FormData();
      formData.append('documentType', documentType);
      
      if (vendorDocumentId) {
        formData.append('vendorDocumentId', vendorDocumentId);
      } else if (file) {
        formData.append('file', file);
      } else {
        throw new Error('Either file or vendorDocumentId must be provided');
      }

      // #region agent log
      if (typeof window !== 'undefined') {
        fetch('http://127.0.0.1:7242/ingest/7c805428-0cbf-4a21-923a-5d22bb0a3d78',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({location:'VendorBidSubmissionPage.tsx:318',message:'Before API upload call',data:{draftSubmissionId:draftSubmission.id,documentType},timestamp:Date.now(),sessionId:'debug-session',runId:'run1',hypothesisId:'B'})}).catch(()=>{});
      }
      // #endregion

      // Optimistically update the query cache with a temporary document
      const tempDocument: BidSubmissionDocument = {
        id: `temp-${Date.now()}`,
        bidSubmissionId: draftSubmission.id,
        fileName: file?.name || 'Uploading...',
        fileSize: file?.size || 0,
        mimeType: file?.type || 'application/octet-stream',
        documentType,
        uploadedAt: new Date().toISOString(),
        status: 'PENDING',
      };

      // Optimistically add to cache
      queryClient.setQueryData<BidSubmissionDocument[]>(
        ['bid-submission-documents', draftSubmission.id],
        (old = []) => [...old, tempDocument]
      );

      let response;
      try {
        response = await bidSubmissionDocumentsApi.uploadDocument(draftSubmission.id, formData);
      } catch (apiError: any) {
        // #region agent log
        if (typeof window !== 'undefined') {
          fetch('http://127.0.0.1:7242/ingest/7c805428-0cbf-4a21-923a-5d22bb0a3d78',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({location:'VendorBidSubmissionPage.tsx:345',message:'API upload call ERROR',data:{error:apiError?.message,responseStatus:apiError?.response?.status,responseData:apiError?.response?.data},timestamp:Date.now(),sessionId:'debug-session',runId:'run1',hypothesisId:'C'})}).catch(()=>{});
        }
        // #endregion
        // Remove optimistic update on error
        queryClient.setQueryData<BidSubmissionDocument[]>(
          ['bid-submission-documents', draftSubmission.id],
          (old = []) => old.filter(doc => doc.id !== tempDocument.id)
        );
        throw apiError;
      }
      
      const document = response.data.document;
      
      // #region agent log
      if (typeof window !== 'undefined') {
        fetch('http://127.0.0.1:7242/ingest/7c805428-0cbf-4a21-923a-5d22bb0a3d78',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({location:'VendorBidSubmissionPage.tsx:357',message:'After API upload call',data:{documentId:document?.id,documentType:document?.documentType,uploadedDocumentsCountBeforeRefetch:uploadedDocuments?.length,allDocumentTypes:uploadedDocuments?.map(d=>d.documentType)},timestamp:Date.now(),sessionId:'debug-session',runId:'run1',hypothesisId:'C'})}).catch(()=>{});
      }
      // #endregion
      
      // Clear the file/selection state first
      setUploadedFiles(prev => {
        const newFiles = { ...prev };
        delete newFiles[requirementId];
        return newFiles;
      });
      setSelectedDocuments(prev => {
        const newDocs = { ...prev };
        delete newDocs[requirementId];
        return newDocs;
      });

      // Replace optimistic update with real document
      queryClient.setQueryData<BidSubmissionDocument[]>(
        ['bid-submission-documents', draftSubmission.id],
        (old = []) => old.map(doc => doc.id === tempDocument.id ? document : doc)
      );

      // #region agent log
      if (typeof window !== 'undefined') {
        fetch('http://127.0.0.1:7242/ingest/7c805428-0cbf-4a21-923a-5d22bb0a3d78',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({location:'VendorBidSubmissionPage.tsx:380',message:'After optimistic update',data:{uploadedDocumentsCountAfterOptimistic:queryClient.getQueryData<BidSubmissionDocument[]>(['bid-submission-documents', draftSubmission.id])?.length},timestamp:Date.now(),sessionId:'debug-session',runId:'run1',hypothesisId:'D'})}).catch(()=>{});
      }
      // #endregion

      // Invalidate and refetch to ensure we have the latest data
      await queryClient.invalidateQueries({ queryKey: ['bid-submission-documents', draftSubmission.id] });
      await refetchDocuments();
      
      // #region agent log
      if (typeof window !== 'undefined') {
        fetch('http://127.0.0.1:7242/ingest/7c805428-0cbf-4a21-923a-5d22bb0a3d78',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({location:'VendorBidSubmissionPage.tsx:387',message:'After refetch',data:{uploadedDocumentsCountAfterRefetch:uploadedDocuments?.length},timestamp:Date.now(),sessionId:'debug-session',runId:'run1',hypothesisId:'D'})}).catch(()=>{});
      }
      // #endregion

      notifications.show({
        title: 'Success',
        message: 'Document uploaded successfully',
        color: 'green',
      });
      
      return document;
    } catch (error: any) {
      console.error('Error uploading document:', error);
      notifications.show({
        title: 'Upload Error',
        message: error.response?.data?.error || 'Failed to upload document',
        color: 'red',
      });
      throw error;
    } finally {
      setUploadingDocuments(prev => ({ ...prev, [requirementId]: false }));
    }
  };

  const submitMutation = useMutation({
    mutationFn: async (values: CreateBidSubmissionDto) => {
      // Check registration requirement
      if (tender?.requireRegistration && registrationStatus?.registration?.status !== 'REGISTERED') {
        throw new Error('You must register for this tender before submitting a bid.');
      }

      if (!draftSubmission?.id) {
        throw new Error('Bid submission not found. Please refresh the page.');
      }

      // Validate that all required documents are uploaded
      if (requirements && requirements.length > 0) {
        const missingDocuments = requirements.filter(requirement => {
          const uploadedDoc = uploadedDocuments?.find(
            doc => doc.documentType === requirement.documentType
          );
          return !uploadedDoc;
        });

        if (missingDocuments.length > 0) {
          throw new Error(`Please upload all required documents. Missing: ${missingDocuments.map(r => getDocumentTypeDisplayName(r.documentType, r.description)).join(', ')}`);
        }
      }

      // Set all document flags to true since all required documents must be submitted
      const documentFlags: Record<string, boolean> = {};
      if (requirements && requirements.length > 0) {
        requirements.forEach(requirement => {
          const fieldName = `has${requirement.documentType === 'CUSTOM' ? 'Custom' : requirement.documentType.charAt(0) + requirement.documentType.slice(1).toLowerCase().replace(/_/g, '')}`;
          documentFlags[fieldName] = true;
        });
      }

      const submissionData = {
        ...values,
        ...documentFlags,
        status: 'SUBMITTED' as const,
        bidItems: bidItems.map(item => ({
          tenderItemId: item.tenderItemId,
          quantity: item.quantity,
          unit: item.unit,
          price: item.price,
          order: item.order,
        })),
      };
      
      // Update existing draft bid submission
      const bidSubmission = await bidSubmissionApi.updateBidSubmission(
        tenderId!,
        draftSubmission.id,
        submissionData,
        true // Use vendor auth
      );

      return bidSubmission;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['bid-submission-draft', tenderId] });
      queryClient.invalidateQueries({ queryKey: ['bid-submissions', tenderId] });
      notifications.show({
        title: 'Success',
        message: 'Bid submitted successfully',
        color: 'green',
      });
      navigate(`/vendor/tenders/${tenderId}`);
    },
    onError: (error: any) => {
      notifications.show({
        title: 'Error',
        message: error.response?.data?.error || error.message || 'Failed to submit bid. Please try again.',
        color: 'red',
      });
    },
  });

  const handleSaveDraft = async () => {
    setIsSavingDraft(true);
    try {
      await saveDraftMutation.mutateAsync(form.values);
    } finally {
      setIsSavingDraft(false);
    }
  };

  const handleSubmit = async (values: CreateBidSubmissionDto) => {
    setIsSubmitting(true);
    try {
      await submitMutation.mutateAsync(values);
    } finally {
      setIsSubmitting(false);
    }
  };

  const getDocumentTypeDisplayName = (documentType: string, description?: string) => {
    if (documentType === 'CUSTOM' && description) {
      return description;
    }

    const displayNames: Record<string, string> = {
      'QUOTATION': 'Quotation',
      'COMPANY_PROFILE': 'Company Profile / Information',
      'REGISTRATION_CERTIFICATE': 'Company Registration Certificate',
      'GST_REGISTRATION': 'GST Registration',
      'EXPERIENCE_LETTERS': 'Experience Letters',
      'TOR': 'Terms of Reference',
      'FINANCIAL_STATEMENT': 'Financial Statement',
      'TAX_CLEARANCE': 'Tax Clearance',
      'INSURANCE': 'Insurance',
      'TECHNICAL_PROPOSAL': 'Technical Proposal',
      'FINANCIAL_PROPOSAL': 'Financial Proposal',
    };
    return displayNames[documentType] || documentType;
  };

  const getAvailableVendorDocuments = (documentType: string) => {
    if (!vendorDocuments) return [];
    
    // Map document types to vendor document types
    const typeMapping: Record<string, string[]> = {
      'BUSINESS_REGISTRATION': ['BUSINESS_REGISTRATION', 'REGISTRATION_CERTIFICATE'],
      'COMPANY_PROFILE': ['COMPANY_PROFILE'],
      'TAX_CLEARANCE': ['TAX_CLEARANCE'],
      'INSURANCE': ['INSURANCE'],
      'FINANCIAL_STATEMENT': ['FINANCIAL_STATEMENT'],
      'EXPERIENCE_LETTERS': ['OTHER'],
      'TECHNICAL_PROPOSAL': ['OTHER'],
      'FINANCIAL_PROPOSAL': ['OTHER'],
    };

    const mappedTypes = typeMapping[documentType] || ['OTHER'];
    return vendorDocuments.filter(doc => mappedTypes.includes(doc.documentType));
  };

  // Check if vendor already has a submitted bid for this tender
  const hasSubmittedBid = existingSubmissionForTender?.status === 'SUBMITTED';

  if (loadingDraft) {
    return (
      <Container size="xl" py="xl">
        <Center>
          <Loader size="lg" />
        </Center>
      </Container>
    );
  }

  // If vendor already has a submitted bid, redirect to view it
  if (hasSubmittedBid && existingSubmissionForTender) {
    return (
      <Container size="xl" py="xl">
        <Paper shadow="sm" p="xl">
          <Alert color="green" title="Bid Already Submitted" icon={<IconCheck size={16} />} mb="md">
            <Text size="sm" mb="md">
              You have already submitted a bid for this tender. Only one submission per tender is allowed.
            </Text>
            <Button
              onClick={() => {
                if (existingSubmissionForTender.tender?.id) {
                  navigate(`/vendor/submissions/${existingSubmissionForTender.tender.id}/${existingSubmissionForTender.id}`);
                } else {
                  navigate(`/vendor/submissions`);
                }
              }}
            >
              View Submission
            </Button>
          </Alert>
        </Paper>
      </Container>
    );
  }

  return (
    <Container size="xl" py="xl">
      <Paper shadow="sm" p="xl">
        <Group mb="xl">
          <Button
            variant="subtle"
            color="green"
            leftSection={<IconArrowLeft size={16} />}
            onClick={() => navigate(`/vendor/tenders/${tenderId}`)}
          >
            Back to Tender Details
          </Button>
        </Group>

        <Title order={2} mb="md">
          Submit Bid
          {publicTender && (
            <Text size="md" c="dimmed" fw={400}>
              {publicTender.referenceNo} - {publicTender.description}
            </Text>
          )}
        </Title>

        {draftSubmission && (
          <Alert color="blue" mb="md" icon={<IconDeviceFloppy size={16} />}>
            <Text size="sm">
              You have a saved draft. Your previous entries have been loaded. You can continue editing or submit when ready.
            </Text>
          </Alert>
        )}

        {tender?.requireRegistration && registrationStatus?.registration?.status !== 'REGISTERED' && (
          <Alert icon={<IconAlertCircle size={16} />} color="red" mb="md" title="Registration Required">
            You must register for this tender before submitting a bid. Please register first.
          </Alert>
        )}

        <form onSubmit={form.onSubmit(handleSubmit)}>
          <Stack gap="md">
            {/* Document Requirements - Show First */}
            <Divider label="REQUIRED DOCUMENTS" labelPosition="center" />

            {/* Bid Items */}
            {loadingItems ? (
              <Center py="md">
                <Loader size="sm" />
              </Center>
            ) : tenderItems && tenderItems.length > 0 ? (
              bidItems.length > 0 ? (
              <Card shadow="sm" p="md">
                <Title order={4} mb="md">Bid Items</Title>
                <Stack gap="md">
                  {bidItems.map((item, index) => (
                    <Card key={item.tenderItemId} shadow="xs" p="md" withBorder>
                      <Stack gap="sm">
                        <Group justify="space-between">
                          <div>
                            <Text fw={500}>{item.itemName}</Text>
                            {item.description && (
                              <Text size="sm" c="dimmed">{item.description}</Text>
                            )}
                          </div>
                          <Badge size="sm" variant="light">
                            #{item.order}
                          </Badge>
                        </Group>

                        <Group grow>
                          <NumberInput
                            label="Quantity"
                            placeholder="Quantity"
                            value={item.quantity}
                            onChange={(value) => updateBidItem(index, 'quantity', value || 1)}
                            min={1}
                          />
                          <TextInput
                            label="Unit"
                            placeholder="Unit"
                            value={item.unit}
                            onChange={(e) => updateBidItem(index, 'unit', e.target.value)}
                          />
                          <NumberInput
                            label="Unit Price"
                            placeholder="Enter unit price"
                            value={item.price}
                            onChange={(value) => updateBidItem(index, 'price', value || 0)}
                            min={0}
                            decimalScale={2}
                            prefix={tender?.currency ? getCurrencySymbol(tender.currency) : 'Rf'}
                            required
                          />
                        </Group>

                        <Text size="sm" c="dimmed">
                          Subtotal: {tender?.currency ? formatCurrency(item.price * item.quantity, tender.currency) : `Rf${(item.price * item.quantity).toFixed(2)}`}
                        </Text>
                      </Stack>
                    </Card>
                  ))}
                </Stack>
              </Card>
              ) : (
                <Text size="sm" c="dimmed">Initializing bid items...</Text>
              )
            ) : (
              <Text size="sm" c="dimmed">No tender items defined for this tender.</Text>
            )}

            {/* Bid Items and Pricing */}
            <Divider label="BID PRICING" labelPosition="center" />
            {loadingRequirements ? (
              <Center py="md">
                <Loader size="sm" />
              </Center>
            ) : requirements && requirements.length > 0 ? (
              <Card shadow="sm" p="md">
                <Title order={4} mb="md">Document Requirements</Title>
                <Table>
                  <Table.Thead>
                    <Table.Tr>
                      <Table.Th>Document Type</Table.Th>
                      <Table.Th>Status</Table.Th>
                      <Table.Th>File Name</Table.Th>
                      <Table.Th>Actions</Table.Th>
                    </Table.Tr>
                  </Table.Thead>
                  <Table.Tbody>
                    {requirements.map((requirement) => {
                      const availableDocs = getAvailableVendorDocuments(requirement.documentType);
                      const hasVendorDocs = availableDocs.length > 0;
                      
                      // Check if document is already uploaded for this requirement
                      const uploadedDoc = uploadedDocuments?.find(
                        doc => doc.documentType === requirement.documentType
                      );

                      return (
                        <Table.Tr key={requirement.id}>
                          <Table.Td>
                            <div>
                              <Text size="sm" fw={500}>
                                {getDocumentTypeDisplayName(requirement.documentType, requirement.description)}
                              </Text>
                              {requirement.description && requirement.documentType !== 'CUSTOM' && (
                                <Text size="xs" c="dimmed">
                                  {requirement.description}
                                </Text>
                              )}
                            </div>
                          </Table.Td>
                          <Table.Td>
                            <Stack gap="xs">
                              <Badge size="xs" color="red" variant="light">Required</Badge>
                              {uploadedDoc ? (
                                <Badge 
                                  size="sm" 
                                  color={
                                    uploadedDoc.status === 'APPROVED' ? 'green' :
                                    uploadedDoc.status === 'REJECTED' ? 'red' : 'yellow'
                                  }
                                  variant="light"
                                  leftSection={
                                    uploadedDoc.status === 'APPROVED' ? <IconCheck size={12} /> :
                                    uploadedDoc.status === 'REJECTED' ? <IconX size={12} /> :
                                    <IconClock size={12} />
                                  }
                                >
                                  {uploadedDoc.status === 'APPROVED' ? 'Approved' :
                                   uploadedDoc.status === 'REJECTED' ? 'Rejected' : 'Pending Review'}
                                </Badge>
                              ) : (
                                <Badge size="sm" color="gray" variant="light">
                                  Not Uploaded
                                </Badge>
                              )}
                              {uploadedDoc?.reviewNotes && (
                                <Text size="xs" c="dimmed">
                                  <strong>Notes:</strong> {uploadedDoc.reviewNotes}
                                </Text>
                              )}
                            </Stack>
                          </Table.Td>
                          <Table.Td>
                            {uploadedDoc ? (
                              <Group gap="xs">
                                <IconFile size={14} />
                                <Text size="sm">{uploadedDoc.fileName}</Text>
                              </Group>
                            ) : (
                              <Text size="sm" c="dimmed">-</Text>
                            )}
                          </Table.Td>
                          <Table.Td>
                            {uploadedDoc ? (
                              <Group gap="xs">
                                <ActionIcon
                                  size="sm"
                                  color="blue"
                                  variant="light"
                                  onClick={async () => {
                                    try {
                                      const response = await bidSubmissionDocumentsApi.downloadDocument(uploadedDoc.id, true); // Use vendor auth
                                      // Create a blob URL and trigger download
                                      const url = window.URL.createObjectURL(new Blob([response.data]));
                                      const link = document.createElement('a');
                                      link.href = url;
                                      link.setAttribute('download', uploadedDoc.fileName);
                                      document.body.appendChild(link);
                                      link.click();
                                      link.remove();
                                      window.URL.revokeObjectURL(url);
                                    } catch (error: any) {
                                      notifications.show({
                                        title: 'Error',
                                        message: error.response?.data?.error || 'Failed to download document',
                                        color: 'red',
                                      });
                                    }
                                  }}
                                  title="Download document"
                                >
                                  <IconDownload size={14} />
                                </ActionIcon>
                                <ActionIcon
                                  size="sm"
                                  color="red"
                                  variant="light"
                                  onClick={async () => {
                                    try {
                                      await bidSubmissionDocumentsApi.removeDocument(uploadedDoc.id);
                                      await queryClient.invalidateQueries({ queryKey: ['bid-submission-documents', draftSubmission?.id] });
                                      await refetchDocuments();
                                      notifications.show({
                                        title: 'Success',
                                        message: 'Document removed',
                                        color: 'green',
                                      });
                                    } catch (error: any) {
                                      notifications.show({
                                        title: 'Error',
                                        message: error.response?.data?.error || 'Failed to remove document',
                                        color: 'red',
                                      });
                                    }
                                  }}
                                  title="Remove document"
                                >
                                  <IconX size={14} />
                                </ActionIcon>
                              </Group>
                            ) : (
                              <Stack gap="xs">
                                {hasVendorDocs && (
                                  <Select
                                    placeholder="Select from profile"
                                    size="xs"
                                    data={[
                                      { value: '', label: 'Upload new' },
                                      ...availableDocs.map(doc => ({
                                        value: doc.id,
                                        label: doc.fileName.length > 30 ? `${doc.fileName.substring(0, 30)}...` : doc.fileName,
                                      })),
                                    ]}
                                    value={selectedDocuments[requirement.id] || ''}
                                    onChange={async (value) => {
                                      if (value && draftSubmission?.id) {
                                        await uploadDocumentForRequirement(
                                          requirement.id,
                                          requirement.documentType,
                                          undefined,
                                          value
                                        );
                                      } else {
                                        setSelectedDocuments({
                                          ...selectedDocuments,
                                          [requirement.id]: value || '',
                                        });
                                      }
                                    }}
                                    disabled={uploadingDocuments[requirement.id]}
                                    style={{ width: 200 }}
                                  />
                                )}
                                
                                {(!selectedDocuments[requirement.id] || !hasVendorDocs) && (
                                  <FileInput
                                    placeholder="Choose file"
                                    size="xs"
                                    required={!selectedDocuments[requirement.id]}
                                    value={uploadedFiles[requirement.id] || null}
                                    onChange={async (file) => {
                                      if (file && draftSubmission?.id) {
                                        await uploadDocumentForRequirement(
                                          requirement.id,
                                          requirement.documentType,
                                          file
                                        );
                                      } else {
                                        setUploadedFiles(prev => ({
                                          ...prev,
                                          [requirement.id]: file,
                                        }));
                                      }
                                    }}
                                    disabled={uploadingDocuments[requirement.id]}
                                    leftSection={<IconFile size={14} />}
                                    accept="application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document,image/*"
                                    style={{ width: 200 }}
                                  />
                                )}
                                
                                {uploadingDocuments[requirement.id] && (
                                  <Text size="xs" c="dimmed">
                                    Uploading...
                                  </Text>
                                )}
                              </Stack>
                            )}
                          </Table.Td>
                        </Table.Tr>
                      );
                    })}
                  </Table.Tbody>
                </Table>
              </Card>
            ) : (
              <Text size="sm" c="dimmed">No document requirements defined for this tender.</Text>
            )}

            <NumberInput
              label="Total Bid Price"
              placeholder="Enter total bid price"
              required
              min={0}
              decimalScale={2}
              prefix={tender?.currency ? getCurrencySymbol(tender.currency) : 'Rf'}
              {...form.getInputProps('totalPrice')}
            />

            {/* Company Information */}
            <Divider label="COMPANY INFORMATION" labelPosition="center" />
            <Card shadow="sm" p="md">
              <Title order={4} mb="md">Company Information</Title>
              <Stack gap="md">
                <TextInput
                  label="Company Name"
                  placeholder="Enter company name"
                  required
                  readOnly
                  {...form.getInputProps('companyName')}
                />

                <Group grow>
                  <TextInput
                    label="Registration Number"
                    placeholder="Company registration number"
                    readOnly
                    {...form.getInputProps('registrationNumber')}
                  />
                  <TextInput
                    label="Tax ID"
                    placeholder="Tax identification number"
                    readOnly
                    {...form.getInputProps('taxId')}
                  />
                </Group>

                <TextInput
                  label="Company Address"
                  placeholder="Full company address"
                  readOnly
                  {...form.getInputProps('companyAddress')}
                />

                <Group grow>
                  <TextInput
                    label="Representative Name"
                    placeholder="Contact person name"
                    {...form.getInputProps('representativeName')}
                  />
                  <TextInput
                    label="Representative Title"
                    placeholder="Job title"
                    {...form.getInputProps('representativeTitle')}
                  />
                </Group>

                <Group grow>
                  <TextInput
                    label="Representative Phone"
                    placeholder="Phone number"
                    {...form.getInputProps('representativePhone')}
                  />
                  <TextInput
                    label="Representative Email"
                    placeholder="Email address"
                    {...form.getInputProps('representativeEmail')}
                  />
                </Group>
              </Stack>
            </Card>

            <Group justify="space-between" mt="md">
              <Button
                variant="light"
                leftSection={<IconDeviceFloppy size={16} />}
                onClick={handleSaveDraft}
                loading={isSavingDraft}
                disabled={tender?.requireRegistration && registrationStatus?.registration?.status !== 'REGISTERED'}
              >
                Save as Draft
              </Button>
              <Group>
                <Button variant="light" onClick={() => navigate(`/vendor/tenders/${tenderId}`)}>
                  Cancel
                </Button>
                <Button
                  type="submit"
                  color="green"
                  loading={isSubmitting}
                  disabled={tender?.requireRegistration && registrationStatus?.registration?.status !== 'REGISTERED'}
                >
                  Submit Bid
                </Button>
              </Group>
            </Group>
          </Stack>
        </form>
      </Paper>
    </Container>
  );
}

